import React, { useState, useEffect } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Tabs,
  Tab,
  Alert,
  Spinner,
  Badge,
  InputGroup,
  Modal,
  Table,
} from "react-bootstrap";
import {
  FaKey,
  FaCreditCard,
  FaMoneyCheckAlt,
  FaSms,
  FaEnvelope,
  FaMapMarkedAlt,
  FaFire,
  FaGlobe,
  FaPlus,
  FaTrash,
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaArrowLeft,
  FaCheckCircle,
  FaSearch,
  FaPlug,
  FaListAlt,
  FaCopy,
  FaPercent,
  FaCalculator,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const ApiSettingsManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [activeTab, setActiveTab] = useState("taxRates");

  // Show/Hide password toggles state
  const [showSecrets, setShowSecrets] = useState({});

  // Custom API Modal state
  const [showApiModal, setShowApiModal] = useState(false);
  const [selectedApi, setSelectedApi] = useState(null);
  const [apiSearchTerm, setApiSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // Backend Routes Catalog state
  const [backendRoutes, setBackendRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(false);
  const [routeSearch, setRouteSearch] = useState("");
  const [methodFilter, setMethodFilter] = useState("ALL");
  const [moduleFilter, setModuleFilter] = useState("ALL");
  const [copiedPath, setCopiedPath] = useState(null);

  // Tax Rates State (Single Collection: configurations)
  const [taxRatesState, setTaxRatesState] = useState({
    gstPercentage: 18,
    serviceTaxPercentage: 0,
    platformFeePercentage: 0,
  });
  const [taxSaving, setTaxSaving] = useState(false);

  const [customApiForm, setCustomApiForm] = useState({
    name: "",
    category: "Custom",
    baseUrl: "",
    apiKey: "",
    apiSecret: "",
    headers: "",
    environment: "production",
    isActive: true,
    notes: "",
  });

  // Form State
  const [apiSettings, setApiSettings] = useState({
    razorpay: {
      keyId: "",
      keySecret: "",
      webhookSecret: "",
      testMode: true,
      forceMock: false,
    },
    cashfree: {
      clientId: "",
      clientSecret: "",
      payoutBaseUrl: "https://sandbox.cashfree.com/payout/v1",
    },
    merchantUpi: {
      upiId: "",
      merchantName: "",
    },
    sms: {
      provider: "fast2sms",
      fast2smsApiKey: "",
      twilioAccountSid: "",
      twilioAuthToken: "",
      twilioVerifyServiceSid: "",
    },
    email: {
      smtpHost: "",
      smtpPort: 587,
      smtpUser: "",
      smtpPass: "",
      emailFrom: "",
      emailFromName: "QuVikme Support",
    },
    maps: {
      googleMapsApiKey: "",
    },
    firebase: {
      projectId: "",
      clientEmail: "",
      databaseUrl: "",
      webApiKey: "",
      serverKey: "",
      senderId: "",
    },
    systemEndpoints: {
      frontendUrl: "http://localhost:3001",
      allowedOrigins: "",
      jwtExpiresIn: "7d",
      customWebhookUrl: "",
    },
    customApis: [],
  });

  const toggleSecretVisibility = (fieldKey) => {
    setShowSecrets((prev) => ({ ...prev, [fieldKey]: !prev[fieldKey] }));
  };

  useEffect(() => {
    fetchApiSettings();
    fetchBackendRoutes();
    fetchTaxRates();
  }, []);

  const fetchApiSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
      const base = rawBase.replace(/\/$/, "");
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const res = await fetch(`${base}/api/admins/api-settings`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to load API settings");
      }

      if (data.apiSettings) {
        setApiSettings((prev) => ({
          ...prev,
          ...data.apiSettings,
          customApis: data.apiSettings.customApis || [],
        }));
      }
    } catch (err) {
      console.error("Error fetching API settings:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackendRoutes = async () => {
    try {
      setRoutesLoading(true);
      const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
      const base = rawBase.replace(/\/$/, "");
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const res = await fetch(`${base}/api/admins/backend-api-routes`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setBackendRoutes(data.routes || []);
      }
    } catch (err) {
      console.error("Error fetching backend API routes catalog:", err);
    } finally {
      setRoutesLoading(false);
    }
  };

  const fetchTaxRates = async () => {
    try {
      const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
      const base = rawBase.replace(/\/$/, "");
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const res = await fetch(`${base}/api/admins/tax-rates`, {
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();
      if (res.ok && data.taxRates) {
        setTaxRatesState({
          gstPercentage: data.taxRates.gstPercentage ?? (data.taxRates.gst * 100) ?? 18,
          serviceTaxPercentage: data.taxRates.serviceTaxPercentage ?? (data.taxRates.serviceTax * 100) ?? 0,
          platformFeePercentage: data.taxRates.platformFeePercentage ?? (data.taxRates.platformFee * 100) ?? 0,
        });
      }
    } catch (err) {
      console.error("Error fetching tax rates:", err);
    }
  };

  const handleSaveTaxRates = async () => {
    try {
      setTaxSaving(true);
      setError(null);
      setSuccessMsg(null);

      const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
      const base = rawBase.replace(/\/$/, "");
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const res = await fetch(`${base}/api/admins/tax-rates`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          gstPercentage: parseFloat(taxRatesState.gstPercentage) || 0,
          serviceTaxPercentage: parseFloat(taxRatesState.serviceTaxPercentage) || 0,
          platformFeePercentage: parseFloat(taxRatesState.platformFeePercentage) || 0,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to update tax rates");
      }

      setSuccessMsg("Tax rates and platform fees saved successfully to database!");
      if (data.taxRates) {
        setTaxRatesState({
          gstPercentage: data.taxRates.gstPercentage ?? (data.taxRates.gst * 100) ?? 18,
          serviceTaxPercentage: data.taxRates.serviceTaxPercentage ?? (data.taxRates.serviceTax * 100) ?? 0,
          platformFeePercentage: data.taxRates.platformFeePercentage ?? (data.taxRates.platformFee * 100) ?? 0,
        });
      }
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error("Error saving tax rates:", err);
      setError(err.message);
    } finally {
      setTaxSaving(false);
    }
  };

  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccessMsg(null);

      const rawBase = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";
      const base = rawBase.replace(/\/$/, "");
      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const res = await fetch(`${base}/api/admins/api-settings`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${adminToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(apiSettings),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to update API settings");
      }

      setSuccessMsg("All API and Integration settings saved successfully!");
      if (data.apiSettings) {
        setApiSettings(data.apiSettings);
      }
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error("Error saving API settings:", err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleNestedChange = (section, field, value) => {
    setApiSettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // Copy path helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedPath(text);
    setTimeout(() => setCopiedPath(null), 2000);
  };

  // Custom API Modal Handlers
  const openCustomApiModal = (api = null) => {
    if (api) {
      setSelectedApi(api);
      setCustomApiForm({
        name: api.name || "",
        category: api.category || "Custom",
        baseUrl: api.baseUrl || "",
        apiKey: api.apiKey || "",
        apiSecret: api.apiSecret || "",
        headers: typeof api.headers === "object" ? JSON.stringify(api.headers, null, 2) : (api.headers || ""),
        environment: api.environment || "production",
        isActive: api.isActive !== false,
        notes: api.notes || "",
      });
    } else {
      setSelectedApi(null);
      setCustomApiForm({
        name: "",
        category: "Custom",
        baseUrl: "",
        apiKey: "",
        apiSecret: "",
        headers: "",
        environment: "production",
        isActive: true,
        notes: "",
      });
    }
    setShowApiModal(true);
  };

  const handleSaveCustomApi = () => {
    if (!customApiForm.name.trim()) {
      alert("API Name is required");
      return;
    }

    const currentApis = [...(apiSettings.customApis || [])];
    if (selectedApi) {
      const updatedList = currentApis.map((a) =>
        a.id === selectedApi.id ? { ...a, ...customApiForm } : a
      );
      setApiSettings((prev) => ({ ...prev, customApis: updatedList }));
    } else {
      const newEntry = {
        id: `api_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
        ...customApiForm,
        createdAt: new Date().toISOString(),
      };
      setApiSettings((prev) => ({ ...prev, customApis: [...currentApis, newEntry] }));
    }

    setShowApiModal(false);
    setSuccessMsg("API configuration updated locally. Click 'Save All API Settings' to persist to database.");
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const handleDeleteCustomApi = (apiId) => {
    if (!window.confirm("Are you sure you want to remove this API configuration?")) return;
    const updatedList = (apiSettings.customApis || []).filter((a) => a.id !== apiId);
    setApiSettings((prev) => ({ ...prev, customApis: updatedList }));
    setSuccessMsg("API configuration removed locally. Click 'Save All API Settings' to commit deletion.");
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const handleToggleCustomApiStatus = (apiId) => {
    const updatedList = (apiSettings.customApis || []).map((a) =>
      a.id === apiId ? { ...a, isActive: !a.isActive } : a
    );
    setApiSettings((prev) => ({ ...prev, customApis: updatedList }));
  };

  // Filtered Backend Routes
  const filteredBackendRoutes = backendRoutes.filter((r) => {
    const matchesMethod = methodFilter === "ALL" || r.method === methodFilter;
    const matchesModule = moduleFilter === "ALL" || r.module === moduleFilter;
    const matchesSearch =
      r.path.toLowerCase().includes(routeSearch.toLowerCase()) ||
      r.description.toLowerCase().includes(routeSearch.toLowerCase()) ||
      r.module.toLowerCase().includes(routeSearch.toLowerCase()) ||
      r.role.toLowerCase().includes(routeSearch.toLowerCase());
    return matchesMethod && matchesModule && matchesSearch;
  });

  // Extract unique modules
  const availableModules = Array.from(new Set(backendRoutes.map((r) => r.module)));

  // Filtered Custom APIs
  const filteredCustomApis = (apiSettings.customApis || []).filter((api) => {
    const matchesCategory = categoryFilter === "All" || api.category === categoryFilter;
    const matchesSearch =
      api.name?.toLowerCase().includes(apiSearchTerm.toLowerCase()) ||
      api.baseUrl?.toLowerCase().includes(apiSearchTerm.toLowerCase()) ||
      api.category?.toLowerCase().includes(apiSearchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getMethodBadgeVariant = (method) => {
    switch (method) {
      case "GET":
        return "primary";
      case "POST":
        return "success";
      case "PUT":
        return "warning";
      case "DELETE":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Tax calculation preview
  const sampleBaseAmount = 1000;
  const sampleGstAmount = (sampleBaseAmount * (parseFloat(taxRatesState.gstPercentage) || 0)) / 100;
  const sampleServiceTaxAmount = (sampleBaseAmount * (parseFloat(taxRatesState.serviceTaxPercentage) || 0)) / 100;
  const samplePlatformFeeAmount = (sampleBaseAmount * (parseFloat(taxRatesState.platformFeePercentage) || 0)) / 100;
  const sampleTotalAmount = sampleBaseAmount + sampleGstAmount + sampleServiceTaxAmount + samplePlatformFeeAmount;

  return (
    <Container fluid className="py-4 px-md-4">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mb-2"
            onClick={() => navigate("/")}
          >
            <FaArrowLeft className="me-1" /> Back to Dashboard
          </Button>
          <h2 className="fw-bold mb-0 d-flex align-items-center gap-2">
            <FaKey className="text-primary" /> Application API Catalog & Configuration Suite
          </h2>
          <p className="text-muted small mb-0">
            Manage tax rates, backend API routes, third-party integrations, and dynamic custom API endpoints.
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          className="px-4 py-2 d-flex align-items-center gap-2 fw-semibold shadow-sm"
          onClick={handleSaveSettings}
          disabled={saving || loading}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" /> Saving...
            </>
          ) : (
            <>
              <FaSave /> Save All API Settings
            </>
          )}
        </Button>
      </div>

      {/* Alert Notifications */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="shadow-sm">
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)} className="shadow-sm">
          <FaCheckCircle className="me-2" /> {successMsg}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Loading complete API configuration suite...</p>
        </div>
      ) : (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="px-3 pt-3 border-bottom bg-light custom-api-tabs"
            >
              {/* Tax Rates & Platform Fees Tab */}
              <Tab
                eventKey="taxRates"
                title={
                  <span className="d-flex align-items-center gap-2 py-1 fw-semibold">
                    <FaPercent className="text-warning" /> Tax Rates & Platform Fees API
                  </span>
                }
              >
                <div className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                      <h5 className="fw-bold mb-1">Tax & Platform Fee Configuration</h5>
                      <p className="text-muted small mb-0">
                        Update GST (%), Service Tax (%), and Platform Fees saved directly in MongoDB single collection.
                      </p>
                    </div>
                    <Button
                      variant="warning"
                      size="sm"
                      className="px-4 py-2 fw-bold d-flex align-items-center gap-2 text-dark shadow-sm"
                      onClick={handleSaveTaxRates}
                      disabled={taxSaving}
                    >
                      {taxSaving ? (
                        <>
                          <Spinner animation="border" size="sm" /> Saving Tax Rates...
                        </>
                      ) : (
                        <>
                          <FaSave /> Save Tax Rates
                        </>
                      )}
                    </Button>
                  </div>

                  <Row className="g-4">
                    <Col lg={7}>
                      <Card className="border p-3 bg-light">
                        <Row className="g-3">
                          <Col md={12}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">GST Rate (%)</Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={taxRatesState.gstPercentage}
                                  onChange={(e) =>
                                    setTaxRatesState({ ...taxRatesState, gstPercentage: e.target.value })
                                  }
                                  placeholder="18"
                                />
                                <InputGroup.Text>%</InputGroup.Text>
                              </InputGroup>
                              <Form.Text className="text-muted">
                                Standard GST applied to booking invoice calculation. (e.g. 18 = 18%)
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">Service Tax Rate (%)</Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={taxRatesState.serviceTaxPercentage}
                                  onChange={(e) =>
                                    setTaxRatesState({ ...taxRatesState, serviceTaxPercentage: e.target.value })
                                  }
                                  placeholder="0"
                                />
                                <InputGroup.Text>%</InputGroup.Text>
                              </InputGroup>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold">Platform Fee Rate (%)</Form.Label>
                              <InputGroup>
                                <Form.Control
                                  type="number"
                                  step="0.1"
                                  min="0"
                                  max="100"
                                  value={taxRatesState.platformFeePercentage}
                                  onChange={(e) =>
                                    setTaxRatesState({ ...taxRatesState, platformFeePercentage: e.target.value })
                                  }
                                  placeholder="0"
                                />
                                <InputGroup.Text>%</InputGroup.Text>
                              </InputGroup>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card>
                    </Col>

                    {/* Tax Calculation Preview Card */}
                    <Col lg={5}>
                      <Card className="border-0 bg-primary bg-opacity-10 p-3 h-100">
                        <h6 className="fw-bold text-primary mb-3 d-flex align-items-center gap-2">
                          <FaCalculator /> Real-Time Tax Calculation Preview
                        </h6>
                        <div className="bg-white p-3 rounded border">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Sample Base Service Amount:</span>
                            <strong className="fw-bold">₹{sampleBaseAmount.toFixed(2)}</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2 text-danger">
                            <span>GST ({taxRatesState.gstPercentage}%):</span>
                            <span>+ ₹{sampleGstAmount.toFixed(2)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2 text-danger">
                            <span>Service Tax ({taxRatesState.serviceTaxPercentage}%):</span>
                            <span>+ ₹{sampleServiceTaxAmount.toFixed(2)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-2 text-danger">
                            <span>Platform Fee ({taxRatesState.platformFeePercentage}%):</span>
                            <span>+ ₹{samplePlatformFeeAmount.toFixed(2)}</span>
                          </div>
                          <hr />
                          <div className="d-flex justify-content-between fs-5 fw-bold text-success">
                            <span>Total Booking Invoice:</span>
                            <span>₹{sampleTotalAmount.toFixed(2)}</span>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </Tab>

              {/* Backend API Catalog Tab */}
              <Tab
                eventKey="backendCatalog"
                title={
                  <span className="d-flex align-items-center gap-2 py-1 fw-semibold">
                    <FaListAlt className="text-primary" /> Backend API Routes Catalog ({backendRoutes.length})
                  </span>
                }
              >
                <div className="p-4">
                  {/* Summary Bar */}
                  <Row className="g-3 mb-4">
                    <Col md={3}>
                      <Card className="border-0 bg-primary bg-opacity-10 p-3 text-center">
                        <small className="text-muted fw-bold text-uppercase">Total APIs</small>
                        <h3 className="fw-bold text-primary mb-0">{backendRoutes.length}</h3>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="border-0 bg-info bg-opacity-10 p-3 text-center">
                        <small className="text-muted fw-bold text-uppercase">GET Endpoints</small>
                        <h3 className="fw-bold text-info mb-0">
                          {backendRoutes.filter((r) => r.method === "GET").length}
                        </h3>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="border-0 bg-success bg-opacity-10 p-3 text-center">
                        <small className="text-muted fw-bold text-uppercase">POST Endpoints</small>
                        <h3 className="fw-bold text-success mb-0">
                          {backendRoutes.filter((r) => r.method === "POST").length}
                        </h3>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="border-0 bg-warning bg-opacity-10 p-3 text-center">
                        <small className="text-muted fw-bold text-uppercase">PUT & DELETE</small>
                        <h3 className="fw-bold text-warning mb-0">
                          {backendRoutes.filter((r) => r.method === "PUT" || r.method === "DELETE").length}
                        </h3>
                      </Card>
                    </Col>
                  </Row>

                  {/* Filters */}
                  <Row className="g-2 mb-3 align-items-center">
                    <Col md={5}>
                      <InputGroup size="sm">
                        <InputGroup.Text>
                          <FaSearch />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search routes by path, method, role, or description..."
                          value={routeSearch}
                          onChange={(e) => setRouteSearch(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={3}>
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted fw-semibold">Method:</small>
                        <Form.Select
                          size="sm"
                          value={methodFilter}
                          onChange={(e) => setMethodFilter(e.target.value)}
                        >
                          <option value="ALL">All Methods</option>
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                          <option value="DELETE">DELETE</option>
                        </Form.Select>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted fw-semibold">Module:</small>
                        <Form.Select
                          size="sm"
                          value={moduleFilter}
                          onChange={(e) => setModuleFilter(e.target.value)}
                        >
                          <option value="ALL">All Feature Modules</option>
                          {availableModules.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </Col>
                  </Row>

                  {/* Routes Table */}
                  {routesLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" size="sm" variant="primary" />
                      <span className="ms-2 text-muted">Indexing backend routes...</span>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover className="align-middle border text-nowrap">
                        <thead className="table-light">
                          <tr>
                            <th>Method</th>
                            <th>API Route Path</th>
                            <th>Feature Module</th>
                            <th>Description</th>
                            <th>Auth Required</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredBackendRoutes.map((route, idx) => (
                            <tr key={idx}>
                              <td>
                                <Badge
                                  bg={getMethodBadgeVariant(route.method)}
                                  className="px-2 py-1"
                                  style={{ width: "55px" }}
                                >
                                  {route.method}
                                </Badge>
                              </td>
                              <td>
                                <code className="text-dark fw-bold fs-6">{route.path}</code>
                              </td>
                              <td>
                                <Badge bg="secondary" className="bg-opacity-75">
                                  {route.module}
                                </Badge>
                              </td>
                              <td className="text-wrap" style={{ maxWidth: "320px" }}>
                                <small className="text-muted">{route.description}</small>
                              </td>
                              <td>
                                <Badge
                                  bg={
                                    route.role.includes("Admin")
                                      ? "dark"
                                      : route.role.includes("Vendor")
                                        ? "info"
                                        : route.role.includes("User")
                                          ? "primary"
                                          : "light"
                                  }
                                  className={route.role === "Public" ? "text-dark border" : ""}
                                >
                                  {route.role}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  title="Copy route path"
                                  onClick={() => copyToClipboard(route.path)}
                                >
                                  <FaCopy className="me-1" />
                                  {copiedPath === route.path ? "Copied!" : "Copy"}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>

              {/* Custom APIs Tab */}
              <Tab
                eventKey="customApis"
                title={
                  <span className="d-flex align-items-center gap-2 py-1 fw-semibold">
                    <FaPlug className="text-success" /> Dynamic Custom APIs (
                    {(apiSettings.customApis || []).length})
                  </span>
                }
              >
                <div className="p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                      <h5 className="fw-bold mb-1">Registered Application APIs</h5>
                      <p className="text-muted small mb-0">
                        Add, modify, or toggle ANY custom API integration across the platform dynamically.
                      </p>
                    </div>
                    <Button
                      variant="success"
                      size="sm"
                      className="px-3 py-2 d-flex align-items-center gap-2 fw-semibold shadow-sm"
                      onClick={() => openCustomApiModal(null)}
                    >
                      <FaPlus /> Add New API Integration
                    </Button>
                  </div>

                  {/* Search and Category Filters */}
                  <Row className="g-2 mb-3">
                    <Col md={6}>
                      <InputGroup size="sm">
                        <InputGroup.Text>
                          <FaSearch />
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Search APIs by name, endpoint, or category..."
                          value={apiSearchTerm}
                          onChange={(e) => setApiSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex align-items-center gap-2 justify-content-md-end">
                        <small className="text-muted fw-semibold">Category Filter:</small>
                        <Form.Select
                          size="sm"
                          value={categoryFilter}
                          onChange={(e) => setCategoryFilter(e.target.value)}
                          style={{ width: "auto" }}
                        >
                          <option value="All">All Categories</option>
                          <option value="Payment">Payment</option>
                          <option value="Logistics">Logistics</option>
                          <option value="Messaging">Messaging / SMS</option>
                          <option value="KYC">KYC / Verification</option>
                          <option value="AI">AI & Analytics</option>
                          <option value="Custom">Custom</option>
                        </Form.Select>
                      </div>
                    </Col>
                  </Row>

                  {/* APIs Table */}
                  {filteredCustomApis.length === 0 ? (
                    <div className="text-center py-5 bg-light rounded border border-dashed">
                      <FaPlug size={40} className="text-muted mb-2" />
                      <h6 className="fw-bold">No Custom APIs Configured</h6>
                      <p className="text-muted small mb-3">
                        Click below to add any custom API integration (e.g. WhatsApp, Logistics, Custom Payment Gateway, OpenAI).
                      </p>
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => openCustomApiModal(null)}
                      >
                        <FaPlus className="me-1" /> Add Custom API Now
                      </Button>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table hover className="align-middle border text-nowrap">
                        <thead className="table-light">
                          <tr>
                            <th>Status</th>
                            <th>API Name</th>
                            <th>Category</th>
                            <th>Target Base URL</th>
                            <th>Environment</th>
                            <th className="text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredCustomApis.map((api) => (
                            <tr key={api.id}>
                              <td>
                                <Form.Check
                                  type="switch"
                                  id={`api-switch-${api.id}`}
                                  checked={api.isActive !== false}
                                  onChange={() => handleToggleCustomApiStatus(api.id)}
                                />
                              </td>
                              <td>
                                <div>
                                  <strong className="d-block">{api.name}</strong>
                                  {api.notes && (
                                    <small className="text-muted d-block text-truncate" style={{ maxWidth: '220px' }}>
                                      {api.notes}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td>
                                <Badge bg="info" className="text-dark">
                                  {api.category || "Custom"}
                                </Badge>
                              </td>
                              <td>
                                <code className="text-primary">{api.baseUrl || "—"}</code>
                              </td>
                              <td>
                                <Badge bg={api.environment === "production" ? "success" : "warning"}>
                                  {api.environment || "production"}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => openCustomApiModal(api)}
                                >
                                  <FaEdit /> Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteCustomApi(api.id)}
                                >
                                  <FaTrash /> Delete
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </Tab>

              {/* Razorpay Tab */}
              <Tab
                eventKey="razorpay"
                title={
                  <span className="d-flex align-items-center gap-2 py-1">
                    <FaCreditCard className="text-primary" /> Razorpay Gateway
                  </span>
                }
              >
                <div className="p-4">
                  <h5 className="fw-bold mb-3 d-flex align-items-center justify-content-between">
                    Razorpay Payment Gateway API
                    <Badge bg={apiSettings.razorpay?.testMode ? "warning" : "success"}>
                      {apiSettings.razorpay?.testMode ? "Test Mode" : "Live Mode"}
                    </Badge>
                  </h5>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Razorpay Key ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.razorpay?.keyId || ""}
                          onChange={(e) => handleNestedChange("razorpay", "keyId", e.target.value)}
                          placeholder="rzp_test_... or rzp_live_..."
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Razorpay Key Secret</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showSecrets["razorpay_secret"] ? "text" : "password"}
                            value={apiSettings.razorpay?.keySecret || ""}
                            onChange={(e) => handleNestedChange("razorpay", "keySecret", e.target.value)}
                            placeholder="Enter Key Secret"
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => toggleSecretVisibility("razorpay_secret")}
                          >
                            {showSecrets["razorpay_secret"] ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Webhook Secret</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showSecrets["razorpay_webhook"] ? "text" : "password"}
                            value={apiSettings.razorpay?.webhookSecret || ""}
                            onChange={(e) => handleNestedChange("razorpay", "webhookSecret", e.target.value)}
                            placeholder="Enter Webhook Secret"
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => toggleSecretVisibility("razorpay_webhook")}
                          >
                            {showSecrets["razorpay_webhook"] ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col md={6} className="d-flex align-items-center">
                      <div className="pt-3">
                        <Form.Check
                          type="switch"
                          id="razorpay-test-mode"
                          label="Test Mode Enabled"
                          checked={!!apiSettings.razorpay?.testMode}
                          onChange={(e) => handleNestedChange("razorpay", "testMode", e.target.checked)}
                          className="fw-semibold mb-2"
                        />
                        <Form.Check
                          type="switch"
                          id="razorpay-force-mock"
                          label="Force Mock Razorpay Service (No live payment required)"
                          checked={!!apiSettings.razorpay?.forceMock}
                          onChange={(e) => handleNestedChange("razorpay", "forceMock", e.target.checked)}
                          className="text-muted small"
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Tab>

              {/* Cashfree & Merchant UPI Tab */}
              <Tab
                eventKey="cashfree"
                title={
                  <span className="d-flex align-items-center gap-2 py-1">
                    <FaMoneyCheckAlt className="text-success" /> Cashfree & Merchant UPI
                  </span>
                }
              >
                <div className="p-4">
                  <h5 className="fw-bold mb-3">Cashfree Payouts API Settings</h5>
                  <Row className="g-3 mb-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Cashfree Client ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.cashfree?.clientId || ""}
                          onChange={(e) => handleNestedChange("cashfree", "clientId", e.target.value)}
                          placeholder="CF..."
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Cashfree Client Secret</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showSecrets["cashfree_secret"] ? "text" : "password"}
                            value={apiSettings.cashfree?.clientSecret || ""}
                            onChange={(e) => handleNestedChange("cashfree", "clientSecret", e.target.value)}
                            placeholder="cfsk_..."
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => toggleSecretVisibility("cashfree_secret")}
                          >
                            {showSecrets["cashfree_secret"] ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Cashfree Payout Base URL</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.cashfree?.payoutBaseUrl || ""}
                          onChange={(e) => handleNestedChange("cashfree", "payoutBaseUrl", e.target.value)}
                          placeholder="https://sandbox.cashfree.com/payout/v1 or https://payout-api.cashfree.com/payout/v1"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr className="my-4" />

                  <h5 className="fw-bold mb-3">Merchant Direct UPI Settings</h5>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Merchant UPI ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.merchantUpi?.upiId || ""}
                          onChange={(e) => handleNestedChange("merchantUpi", "upiId", e.target.value)}
                          placeholder="merchant@upi"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Merchant Full Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.merchantUpi?.merchantName || ""}
                          onChange={(e) => handleNestedChange("merchantUpi", "merchantName", e.target.value)}
                          placeholder="Business / Owner Name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab>

              {/* SMS Tab */}
              <Tab
                eventKey="sms"
                title={
                  <span className="d-flex align-items-center gap-2 py-1">
                    <FaSms className="text-info" /> SMS & OTP APIs
                  </span>
                }
              >
                <div className="p-4">
                  <h5 className="fw-bold mb-3">SMS & Phone OTP Provider Configuration</h5>
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Active SMS Provider</Form.Label>
                        <Form.Select
                          value={apiSettings.sms?.provider || "fast2sms"}
                          onChange={(e) => handleNestedChange("sms", "provider", e.target.value)}
                        >
                          <option value="fast2sms">Fast2SMS (India)</option>
                          <option value="twilio">Twilio (Global)</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    {apiSettings.sms?.provider === "fast2sms" ? (
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Fast2SMS API Key</Form.Label>
                          <InputGroup>
                            <Form.Control
                              type={showSecrets["fast2sms"] ? "text" : "password"}
                              value={apiSettings.sms?.fast2smsApiKey || ""}
                              onChange={(e) => handleNestedChange("sms", "fast2smsApiKey", e.target.value)}
                              placeholder="Enter Fast2SMS API Key"
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={() => toggleSecretVisibility("fast2sms")}
                            >
                              {showSecrets["fast2sms"] ? <FaEyeSlash /> : <FaEye />}
                            </Button>
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    ) : (
                      <>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold">Twilio Account SID</Form.Label>
                            <Form.Control
                              type="text"
                              value={apiSettings.sms?.twilioAccountSid || ""}
                              onChange={(e) => handleNestedChange("sms", "twilioAccountSid", e.target.value)}
                              placeholder="AC..."
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold">Twilio Auth Token</Form.Label>
                            <InputGroup>
                              <Form.Control
                                type={showSecrets["twilio_auth"] ? "text" : "password"}
                                value={apiSettings.sms?.twilioAuthToken || ""}
                                onChange={(e) => handleNestedChange("sms", "twilioAuthToken", e.target.value)}
                                placeholder="Enter Twilio Auth Token"
                              />
                              <Button
                                variant="outline-secondary"
                                onClick={() => toggleSecretVisibility("twilio_auth")}
                              >
                                {showSecrets["twilio_auth"] ? <FaEyeSlash /> : <FaEye />}
                              </Button>
                            </InputGroup>
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group>
                            <Form.Label className="fw-semibold">Twilio Verify Service SID</Form.Label>
                            <Form.Control
                              type="text"
                              value={apiSettings.sms?.twilioVerifyServiceSid || ""}
                              onChange={(e) => handleNestedChange("sms", "twilioVerifyServiceSid", e.target.value)}
                              placeholder="VA..."
                            />
                          </Form.Group>
                        </Col>
                      </>
                    )}
                  </Row>
                </div>
              </Tab>

              {/* Email Tab */}
              <Tab
                eventKey="email"
                title={
                  <span className="d-flex align-items-center gap-2 py-1">
                    <FaEnvelope className="text-warning" /> Email & SMTP
                  </span>
                }
              >
                <div className="p-4">
                  <h5 className="fw-bold mb-3">SMTP Mail Server Settings</h5>
                  <Row className="g-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">SMTP Host</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.email?.smtpHost || ""}
                          onChange={(e) => handleNestedChange("email", "smtpHost", e.target.value)}
                          placeholder="smtp.gmail.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">SMTP Port</Form.Label>
                        <Form.Control
                          type="number"
                          value={apiSettings.email?.smtpPort || 587}
                          onChange={(e) => handleNestedChange("email", "smtpPort", parseInt(e.target.value) || 587)}
                          placeholder="587"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">SMTP Username / Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={apiSettings.email?.smtpUser || ""}
                          onChange={(e) => handleNestedChange("email", "smtpUser", e.target.value)}
                          placeholder="support@quvikme.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">SMTP Password / App Password</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showSecrets["smtp_pass"] ? "text" : "password"}
                            value={apiSettings.email?.smtpPass || ""}
                            onChange={(e) => handleNestedChange("email", "smtpPass", e.target.value)}
                            placeholder="Enter App Password"
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => toggleSecretVisibility("smtp_pass")}
                          >
                            {showSecrets["smtp_pass"] ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">From Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          value={apiSettings.email?.emailFrom || ""}
                          onChange={(e) => handleNestedChange("email", "emailFrom", e.target.value)}
                          placeholder="no-reply@quvikme.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Sender Display Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.email?.emailFromName || ""}
                          onChange={(e) => handleNestedChange("email", "emailFromName", e.target.value)}
                          placeholder="QuVikme Support"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab>

              {/* Maps Tab */}
              <Tab
                eventKey="maps"
                title={
                  <span className="d-flex align-items-center gap-2 py-1">
                    <FaMapMarkedAlt className="text-success" /> Google Maps API
                  </span>
                }
              >
                <div className="p-4">
                  <h5 className="fw-bold mb-3">Google Maps & Location APIs</h5>
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Google Maps Web/Places API Key</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.maps?.googleMapsApiKey || ""}
                          onChange={(e) => handleNestedChange("maps", "googleMapsApiKey", e.target.value)}
                          placeholder="AIzaSy..."
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab>

              {/* Firebase Tab */}
              <Tab
                eventKey="firebase"
                title={
                  <span className="d-flex align-items-center gap-2 py-1">
                    <FaFire className="text-danger" /> Firebase & Push Notifications
                  </span>
                }
              >
                <div className="p-4">
                  <h5 className="fw-bold mb-3">Firebase Project & Push Notification API</h5>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Firebase Project ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.firebase?.projectId || ""}
                          onChange={(e) => handleNestedChange("firebase", "projectId", e.target.value)}
                          placeholder="quvikmetechsolutions-ecc02"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Firebase Client Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={apiSettings.firebase?.clientEmail || ""}
                          onChange={(e) => handleNestedChange("firebase", "clientEmail", e.target.value)}
                          placeholder="firebase-adminsdk@..."
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Database URL</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.firebase?.databaseUrl || ""}
                          onChange={(e) => handleNestedChange("firebase", "databaseUrl", e.target.value)}
                          placeholder="quvikmetechsolutions-ecc02"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Web API Key</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.firebase?.webApiKey || ""}
                          onChange={(e) => handleNestedChange("firebase", "webApiKey", e.target.value)}
                          placeholder="AIzaSy..."
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">FCM Server Key</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type={showSecrets["fcm_server_key"] ? "text" : "password"}
                            value={apiSettings.firebase?.serverKey || ""}
                            onChange={(e) => handleNestedChange("firebase", "serverKey", e.target.value)}
                            placeholder="AAAA..."
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => toggleSecretVisibility("fcm_server_key")}
                          >
                            {showSecrets["fcm_server_key"] ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">FCM Sender ID</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.firebase?.senderId || ""}
                          onChange={(e) => handleNestedChange("firebase", "senderId", e.target.value)}
                          placeholder="1234567890..."
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab>

              {/* System & Webhooks Tab */}
              <Tab
                eventKey="system"
                title={
                  <span className="d-flex align-items-center gap-2 py-1">
                    <FaGlobe className="text-secondary" /> Webhooks & System URLs
                  </span>
                }
              >
                <div className="p-4">
                  <h5 className="fw-bold mb-3">System URLs & Webhook API Endpoints</h5>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Frontend App URL</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.systemEndpoints?.frontendUrl || ""}
                          onChange={(e) => handleNestedChange("systemEndpoints", "frontendUrl", e.target.value)}
                          placeholder="http://localhost:3001 or https://app.quvikme.com"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Allowed CORS Origins (Comma-separated)</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.systemEndpoints?.allowedOrigins || ""}
                          onChange={(e) => handleNestedChange("systemEndpoints", "allowedOrigins", e.target.value)}
                          placeholder="https://admin.quvikme.com, https://app.quvikme.com"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">JWT Session Expiration</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.systemEndpoints?.jwtExpiresIn || "7d"}
                          onChange={(e) => handleNestedChange("systemEndpoints", "jwtExpiresIn", e.target.value)}
                          placeholder="7d"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Custom System Webhook Endpoint URL</Form.Label>
                        <Form.Control
                          type="text"
                          value={apiSettings.systemEndpoints?.customWebhookUrl || ""}
                          onChange={(e) => handleNestedChange("systemEndpoints", "customWebhookUrl", e.target.value)}
                          placeholder="https://your-webhook-listener.com/api/v1/events"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Tab>
            </Tabs>

            <div className="p-3 bg-light border-top text-end">
              <Button
                variant="primary"
                size="md"
                className="px-4 py-2"
                onClick={handleSaveSettings}
                disabled={saving || loading}
              >
                {saving ? "Saving Changes..." : "Save All API Settings"}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Add / Edit Custom API Modal */}
      <Modal show={showApiModal} onHide={() => setShowApiModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedApi ? "Edit API Integration" : "Add New API Integration"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Label className="fw-semibold">API Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g., WhatsApp Business API, Shiprocket, OpenAI API"
                    value={customApiForm.name}
                    onChange={(e) => setCustomApiForm({ ...customApiForm, name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Category</Form.Label>
                  <Form.Select
                    value={customApiForm.category}
                    onChange={(e) => setCustomApiForm({ ...customApiForm, category: e.target.value })}
                  >
                    <option value="Custom">Custom</option>
                    <option value="Payment">Payment</option>
                    <option value="Logistics">Logistics</option>
                    <option value="Messaging">Messaging / SMS</option>
                    <option value="KYC">KYC / Verification</option>
                    <option value="AI">AI & Analytics</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Target Base URL / Endpoint</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="https://api.example.com/v1"
                    value={customApiForm.baseUrl}
                    onChange={(e) => setCustomApiForm({ ...customApiForm, baseUrl: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">API Key / Client ID</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter API Key"
                    value={customApiForm.apiKey}
                    onChange={(e) => setCustomApiForm({ ...customApiForm, apiKey: e.target.value })}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">API Secret / Token</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showSecrets["modal_api_secret"] ? "text" : "password"}
                      placeholder="Enter Secret Key"
                      value={customApiForm.apiSecret}
                      onChange={(e) => setCustomApiForm({ ...customApiForm, apiSecret: e.target.value })}
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => toggleSecretVisibility("modal_api_secret")}
                    >
                      {showSecrets["modal_api_secret"] ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Environment</Form.Label>
                  <Form.Select
                    value={customApiForm.environment}
                    onChange={(e) => setCustomApiForm({ ...customApiForm, environment: e.target.value })}
                  >
                    <option value="production">Production</option>
                    <option value="sandbox">Sandbox / Testing</option>
                    <option value="development">Development</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6} className="d-flex align-items-center">
                <div className="pt-3">
                  <Form.Check
                    type="switch"
                    id="custom-api-status"
                    label="API Integration Active"
                    checked={customApiForm.isActive}
                    onChange={(e) => setCustomApiForm({ ...customApiForm, isActive: e.target.checked })}
                    className="fw-semibold"
                  />
                </div>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Custom HTTP Headers (JSON or key=value)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder='{"X-Custom-Header": "Value", "Authorization": "Bearer ..."} '
                    value={customApiForm.headers}
                    onChange={(e) => setCustomApiForm({ ...customApiForm, headers: e.target.value })}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Notes / Purpose</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Brief description of what this API does in the application..."
                    value={customApiForm.notes}
                    onChange={(e) => setCustomApiForm({ ...customApiForm, notes: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApiModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveCustomApi}>
            {selectedApi ? "Update API Entry" : "Add API Entry"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ApiSettingsManagement;
